import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Lock, ShieldCheck } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from 'components/ui/button';
import formationDraftService from 'services/formationDraftService';
import {
  FORMATION_PLANS,
  FormationDraftDetails,
  FormationPlanGrid,
} from './FormationClaimSections';

function errorMessage(error) {
  if (error?.response?.status === 403) {
    return 'Verifica tu email antes de continuar. Debes ingresar con el mismo email que recibió la invitación.';
  }
  if (error?.response?.status === 404) {
    return 'Esta invitación no es válida, ya fue utilizada o venció.';
  }
  return (
    error?.response?.data?.message ||
    error?.message ||
    'No pudimos continuar. Intenta nuevamente.'
  );
}

const FormationClaimPage = () => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const started = useRef(false);
  const [draft, setDraft] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('essential');
  const [checkout, setCheckout] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');

  const token = searchParams.get('token');
  const draftId = searchParams.get('draftId');

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    async function load() {
      try {
        const result = token
          ? await formationDraftService.claim(token)
          : draftId
          ? await formationDraftService.get(draftId)
          : null;
        if (!result) throw new Error('Falta la invitación de formación.');
        setDraft(result);
        if (token) {
          setSearchParams({ draftId: result.draftId }, { replace: true });
        }
      } catch (requestError) {
        setError(errorMessage(requestError));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [draftId, setSearchParams, token]);

  const plan = useMemo(
    () => FORMATION_PLANS.find((item) => item.code === selectedPlan),
    [selectedPlan]
  );
  const discountedTotal = Math.max(
    0,
    plan.price - (draft?.discountCents || 0) / 100
  );
  const formationAlreadyPaid = [
    'paid',
    'formation_in_progress',
    'documents_ready',
    'completed',
  ].includes(draft?.state);

  const prepareCheckout = async () => {
    setError('');
    setPaying(true);
    try {
      const result = await formationDraftService.checkout(
        draft.draftId,
        selectedPlan
      );
      setCheckout(result);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setPaying(false);
    }
  };

  const submitPayment = async (event) => {
    event.preventDefault();
    if (!stripe || !elements || !checkout?.clientSecret) return;
    setError('');
    setPaying(true);
    try {
      const card = elements.getElement(CardElement);
      const { error: stripeError, paymentIntent } =
        await stripe.confirmCardPayment(checkout.clientSecret, {
          payment_method: { card },
        });
      if (stripeError) throw stripeError;
      if (paymentIntent?.status !== 'succeeded') {
        throw new Error('El pago todavía no fue confirmado.');
      }
      await formationDraftService.confirmPayment(
        paymentIntent.id,
        checkout.registrationId
      );
      navigate('/empresa/home', { replace: true });
    } catch (paymentError) {
      setError(errorMessage(paymentError));
    } finally {
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-neutral-50 dark:bg-neutral-950">
        <p className="text-sm text-neutral-600 dark:text-neutral-300">
          Preparando tu formación…
        </p>
      </main>
    );
  }

  if (!draft) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-neutral-50 px-6 dark:bg-neutral-950">
        <div className="max-w-md rounded-2xl border border-neutral-200 bg-white p-8 text-center dark:border-neutral-800 dark:bg-neutral-900">
          <h1 className="text-2xl font-semibold text-neutral-950 dark:text-white">
            No pudimos abrir la invitación
          </h1>
          <p className="mt-3 text-sm text-neutral-600 dark:text-neutral-300">
            {error}
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-neutral-50 px-4 py-10 dark:bg-neutral-950">
      <div className="mx-auto max-w-5xl">
        <header className="mb-8 flex flex-col gap-4 rounded-2xl border border-neutral-200 bg-white p-7 dark:border-neutral-800 dark:bg-neutral-900">
          <div className="text-emerald-700 dark:text-emerald-300 flex items-center gap-3 text-sm font-medium">
            <ShieldCheck className="h-5 w-5" />
            Datos recibidos de forma segura
          </div>
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-neutral-950 dark:text-white">
              Revisa el plan para {draft.companyName}
            </h1>
            <p className="mt-2 text-neutral-600 dark:text-neutral-300">
              Rely gestionará el pago, la formación, las notificaciones y tus
              documentos.
            </p>
          </div>
          {draft.discountCents > 0 && (
            <div className="bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200 w-fit rounded-full px-4 py-2 text-sm font-semibold">
              Descuento de partner aplicado: $
              {(draft.discountCents / 100).toFixed(0)}
            </div>
          )}
        </header>

        <FormationPlanGrid
          selectedPlan={selectedPlan}
          onSelectPlan={(planCode) => {
            setSelectedPlan(planCode);
            setCheckout(null);
          }}
        />

        <FormationDraftDetails draft={draft} />

        <section className="mt-6 rounded-2xl border border-neutral-200 bg-white p-7 dark:border-neutral-800 dark:bg-neutral-900">
          <div className="flex items-center justify-between">
            <span className="text-neutral-600 dark:text-neutral-300">
              Total
            </span>
            <span className="text-3xl font-semibold text-neutral-950 dark:text-white">
              ${discountedTotal}
            </span>
          </div>
          {error && (
            <p className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
              {error}
            </p>
          )}
          {formationAlreadyPaid ? (
            <Button
              className="mt-6 w-full"
              variant="primary"
              onClick={() => navigate('/empresa/home')}
            >
              Ir a mi dashboard
            </Button>
          ) : (
            <>
              <label className="mt-5 flex items-start gap-3 text-sm text-neutral-700 dark:text-neutral-300">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(event) => setTermsAccepted(event.target.checked)}
                  className="accent-emerald-600 mt-1 h-4 w-4 rounded border-neutral-300"
                />
                <span>
                  Confirmo que revisé la información y acepto los{' '}
                  <a
                    className="underline"
                    href="/terms"
                    target="_blank"
                    rel="noreferrer"
                  >
                    términos de Rely
                  </a>
                  .
                </span>
              </label>
              {!checkout ? (
                <Button
                  className="mt-6 w-full"
                  variant="primary"
                  onClick={prepareCheckout}
                  disabled={paying || !termsAccepted}
                >
                  <Lock />
                  {paying ? 'Preparando pago…' : 'Continuar al pago seguro'}
                </Button>
              ) : (
                <form className="mt-6" onSubmit={submitPayment}>
                  <div className="rounded-xl border border-neutral-300 p-4 dark:border-neutral-700 dark:bg-white">
                    <CardElement options={{ hidePostalCode: false }} />
                  </div>
                  <Button
                    className="mt-4 w-full"
                    variant="primary"
                    type="submit"
                    disabled={paying || !stripe}
                  >
                    {paying
                      ? 'Confirmando…'
                      : `Pagar $${(checkout.totalCents / 100).toFixed(0)}`}
                  </Button>
                </form>
              )}
            </>
          )}
        </section>
      </div>
    </main>
  );
};

export default FormationClaimPage;
